import React from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Typography } from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../generic/hooks/useDateFormatter'
import JobStatusChip from '../JobStatusChip'
import { activeDeviceCount, formatSince, kpiMachine, progressPercent } from '../../utils/jobDetailFormat'
import { EllipsisText, OVERLINE_SX, Pill, ProgressBar } from './JobDetailPrimitives'

// Historia 160 (CA1.8-CA1.12, mock 1) — la tira de CINCO KPIs, siempre los cinco y en este orden:
// ESTADO · PROGRESO · PIEZAS · MÁQUINA · INCIDENCIAS. El de incidencias no desaparece sin abiertas
// (D18): dice "Sin incidencias abiertas" en neutro para que la tira no cambie de forma (S12).
// E-9 (RN57): el KPI MÁQUINA ya no pinta "+k" junto al nombre; dice "N en ejecución" (máquinas con el
// job activo) o "Sin ejecución activa".

const Kpi = ({ title, children, testId }) => (
  <Box
    component='section'
    aria-label={title}
    data-testid={testId}
    sx={{ flex: '1 1 0', minWidth: 0, px: 3, py: 2, borderRight: 1, borderColor: 'divider', '&:last-of-type': { borderRight: 0 } }}
  >
    <Typography component='h3' sx={{ ...OVERLINE_SX, mb: 0.75 }}>{title}</Typography>
    {children}
  </Box>
)

Kpi.propTypes = { title: PropTypes.string, children: PropTypes.node, testId: PropTypes.string }

const BIG_SX = { fontSize: '1.9rem', fontWeight: 800, lineHeight: 1.1 }

const JobDetailKpiStrip = ({ detail = {} }) => {
  const { t_ } = useSmartTranslation()
  const formatter = useDateFormatter()
  const job = detail.job || {}
  const quantities = detail.quantities || {}
  const incidences = detail.summary?.incidences || {}
  const finished = quantities.finished || 0
  const total = quantities.orderQuantity || 0
  const percent = progressPercent(finished, total)
  const machine = kpiMachine(detail.plannedDevice, detail.devices || [])
  const since = formatSince(job.statusSince, formatter)
  const machineGroups = machine.groups.join(', ')
  const running = activeDeviceCount(detail.devices || [])

  return (
    <Stack direction='row' sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider' }} data-testid='job-detail-kpis'>
      <Kpi title={t_('Status')} testId='kpi-status'>
        {job.status ? <JobStatusChip status={job.status} statusType={job.statusType} /> : null}
        {since !== ''
          ? <Typography color='text.secondary' sx={{ mt: 0.75 }} data-testid='kpi-status-since'>{t_('Since {{time}}', { time: since })}</Typography>
          : null}
      </Kpi>
      <Kpi title={t_('Progress')} testId='kpi-progress'>
        <Stack direction='row' alignItems='center' spacing={2}>
          <Typography sx={BIG_SX} data-testid='kpi-progress-percent'>{percent} %</Typography>
          <ProgressBar value={percent} sx={{ flexGrow: 1 }} label={t_('Progress')} />
        </Stack>
        <Typography color='text.secondary' sx={{ mt: 0.5 }}>{t_('{{done}} of {{total}} parts', { done: finished, total })}</Typography>
      </Kpi>
      <Kpi title={t_('Parts')} testId='kpi-parts'>
        <Typography sx={BIG_SX} data-testid='kpi-parts-ratio'>{finished} / {total}</Typography>
        <Typography color='text.secondary' sx={{ mt: 0.5 }} data-testid='kpi-parts-detail'>
          {[
            t_('{{count}} correct', { count: finished }),
            t_('{{count}} non-conforming', { count: quantities.nonConforming || 0 }),
            t_('{{count}} available', { count: quantities.available || 0 })
          ].join(' · ')}
        </Typography>
      </Kpi>
      <Kpi title={t_('Machine')} testId='kpi-machine'>
        {machine.present
          ? (
            <>
              <Typography sx={{ fontSize: '1.3rem', fontWeight: 800 }} data-testid='kpi-machine-name'>{machine.name}</Typography>
              {machineGroups !== '' ? <EllipsisText text={machineGroups} sx={{ color: 'text.secondary', mt: 0.5 }} testId='kpi-machine-groups' /> : null}
            </>
            )
          : <Typography sx={{ fontSize: '1.3rem', fontWeight: 800 }} data-testid='kpi-machine-name'>{t_('Not planned')}</Typography>}
        <Typography sx={{ mt: 0.5, fontWeight: 700, color: running > 0 ? 'info.light' : 'text.secondary' }} data-testid='kpi-machine-running'>
          {running > 0 ? t_('{{count}} running', { count: running }) : t_('No active execution')}
        </Typography>
      </Kpi>
      <Kpi title={t_('Incidences')} testId='kpi-incidences'>
        {(incidences.open || 0) > 0
          ? (
            <>
              <Pill label={t_('{{count}} open', { count: incidences.open })} tone='error' icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />} testId='kpi-incidences-open' />
              {(incidences.pendingActions || 0) > 0
                ? <Typography sx={{ mt: 0.75, color: 'error.light', fontWeight: 700 }} data-testid='kpi-incidences-pending'>{t_('{{count}} pending actions', { count: incidences.pendingActions })}</Typography>
                : null}
            </>
            )
          : <Typography color='text.secondary' data-testid='kpi-incidences-none' data-tone='neutral'>{t_('No open incidences')}</Typography>}
      </Kpi>
    </Stack>
  )
}

JobDetailKpiStrip.propTypes = { detail: PropTypes.object }

export default React.memo(JobDetailKpiStrip)
